import React from 'react';
import { Button, Tooltip } from 'antd';
import { BulbOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import styled from 'styled-components';
import moment from 'moment';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import { filterSelector } from '@store/common/selectors';
import { sliceAreaAtom } from '@src/components/metrics-panel-hz/atoms/index';
import { buildSliceDiagnosticsRequest } from '../utils/sliceContextUtils';
import { navigateToNexaWithRequest } from '../utils/navigateToNexa';
import { logger } from '@src/utils/misc/logger';
import { CHART_TYPE } from '../hooks/utils';
import { useNexaGateCheck, NexaGateState } from '@src/components/nexa/utils/nexaGateCheck';

const FloatingButton = styled(Button)`
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 10;
  box-shadow: 0 2px 8px rgb(0 0 0 / 15%);

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgb(0 0 0 / 20%);
  }
`;

interface AskNexaButtonProps {
  metric: string;
  chartMode: string;
  isSelected: boolean;
  diagMode: boolean;
}

/**
 * Floating "Ask Nexa" button that appears when a chart slice exists
 * Self-contained component with all visibility logic inside
 * Uses Redux chartIntervalSelector which already contains the sliced timestamps
 */
const AskNexaButton: React.FC<AskNexaButtonProps> = ({
  metric,
  chartMode,
  isSelected,
  diagMode,
}) => {
  const navigate = useNavigate();
  const interval = useSelector(chartIntervalSelector) as any; // Interval type from selector
  const filterRules = useSelector(filterSelector);
  const sliceArea = useAtomValue(sliceAreaAtom) as any; // Simple percentage coordinates [start, end]
  const { state: nexaGateState } = useNexaGateCheck();

  // All visibility logic contained here - reduces MetricCard re-renders
  const hasActiveSlice = sliceArea && sliceArea.length > 0;
  const isNexaEnabled = nexaGateState === NexaGateState.ALLOWED;
  // force disabled for now
  const shouldShow =
    chartMode === CHART_TYPE.LINE &&
    hasActiveSlice &&
    !diagMode &&
    isSelected &&
    isNexaEnabled &&
    false; // NOTE: hardcoded FF for now

  const handleClick = () => {
    try {
      if (!metric || !interval || !interval.from || !interval.to) {
        logger.error('[AskNexaButton] Missing required context for Ask Nexa.');
        return;
      }

      // chartIntervalSelector already returns the sliced interval if brush is active
      const request = buildSliceDiagnosticsRequest(
        metric,
        {
          from: moment(interval.from).toISOString(),
          to: moment(interval.to).toISOString(),
        },
        filterRules,
        interval.granularity,
      );

      logger.log('[AskNexaButton] Navigating to Nexa with request:', request);
      navigateToNexaWithRequest(request, navigate);
    } catch (error) {
      logger.error('[AskNexaButton] Failed to build request:', error);
    }
  };

  // Return null early if shouldn't show - no DOM rendering
  if (!shouldShow) {
    return null;
  }

  return (
    <Tooltip title="Ask Nexa to analyze this spike" placement="left">
      <FloatingButton type="primary" icon={<BulbOutlined />} onClick={handleClick} size="small">
        Ask Nexa
      </FloatingButton>
    </Tooltip>
  );
};

export default AskNexaButton;
